// The menu badges that depend on the deployment profile: the drift of each ecosystem it serves, and
// the receiver connections in error where it receives. Until the profile is read, or when it
// failed, they read nothing and show nothing.
import { useQueries, useQuery } from '@tanstack/react-query';
import { Option, Result } from 'effect';
import type { PowerDeploymentProfile } from '@finnest/power-admin-contracts';
import { servedEcosystemsOf } from '../../deployment/deployment-gate.tsx';
import { deploymentProfileQueryOptions } from '../../deployment/edge.ts';
import { receives } from '../../inicio/overview/receiver-card.tsx';
import { connectionCountQueryOptions } from '../../receptora/client/edge.ts';
import { driftSnapshotQueryOptions } from '../../regulatory-drift/client/edge.ts';
import { driftBadge, receiverBadge } from './nav-counts.ts';
import type { NavBadge } from './nav-counts.ts';

const useDeploymentProfile = (): Option.Option<PowerDeploymentProfile> => {
  const profileQuery = useQuery(deploymentProfileQueryOptions);
  return profileQuery.status === 'success' && Result.isSuccess(profileQuery.data)
    ? Option.some(profileQuery.data.success)
    : Option.none();
};

/** The endpoints in divergence, once every served ecosystem's snapshot was read. */
export const useDriftBadge = (): Option.Option<NavBadge> => {
  const ecosystems = Option.match(useDeploymentProfile(), {
    onNone: () => [],
    onSome: servedEcosystemsOf,
  });
  const queries = useQueries({
    queries: ecosystems.map((ecosystem) => driftSnapshotQueryOptions(ecosystem)),
  });
  const snapshots = queries.flatMap((query) =>
    query.status === 'success' && Result.isSuccess(query.data) ? [query.data.success] : [],
  );
  return snapshots.length === ecosystems.length ? driftBadge(snapshots) : Option.none();
};

/** The receiver connections in error, read only where the deployment receives. */
export const useReceiverBadge = (): Option.Option<NavBadge> => {
  const receiving = Option.exists(useDeploymentProfile(), receives);
  const query = useQuery({ ...connectionCountQueryOptions('error'), enabled: receiving });
  return receiving && query.status === 'success' && Result.isSuccess(query.data)
    ? receiverBadge(query.data.success)
    : Option.none();
};
